import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Sparkles, CalendarPlus, Trash2, RefreshCw, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { PLATFORMS, FORMATS, OBJECTIVES, formatLabel, platformLabel } from "@/lib/constants";
import { toast } from "sonner";

function ChipGroup({ options, values, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button key={o.id} type="button" data-testid={`${testid}-${o.id}`}
          onClick={() => onChange(values.includes(o.id) ? values.filter((v) => v !== o.id) : [...values, o.id])}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
            values.includes(o.id) ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300"}`}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function IdeaGenerator({ clientId, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState("8");
  const [networks, setNetworks] = useState(["instagram"]);
  const [formats, setFormats] = useState(["reel", "carrousel", "photo"]);
  const [objectives, setObjectives] = useState(["engagement", "notoriete"]);
  const [month, setMonth] = useState(format(new Date(), "MMMM yyyy", { locale: fr }));
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/ai/clients/${clientId}/ideas/generate`, {
        count: Number(count), networks, formats, objectives, month, description: description.trim(),
      });
      toast.success(`${data.length} idées générées pour ${month}`);
      setOpen(false);
      onGenerated?.(data);
    } catch { toast.error("La génération a échoué, réessayez"); }
    finally { setLoading(false); }
  };

  if (!open) {
    return (
      <Button data-testid="generate-ideas-btn" onClick={() => setOpen(true)}
        className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5">
        <Sparkles className="w-4 h-4" /> Générer des idées avec l'IA
      </Button>
    );
  }
  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-5 space-y-4 animate-fade-up" data-testid="idea-generator">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-bold">Paramètres de génération</h3>
        <button onClick={() => setOpen(false)} className="text-xs text-neutral-400 hover:text-neutral-900">Fermer</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Nombre de contenus</Label>
          <Select value={count} onValueChange={setCount}>
            <SelectTrigger data-testid="idea-count" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["4", "8", "12", "16"].map((n) => <SelectItem key={n} value={n}>{n} contenus</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Mois ciblé</Label>
          <input data-testid="idea-month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="mt-1.5 w-full text-sm border border-neutral-200 rounded-md px-3 py-2 bg-white" />
        </div>
      </div>
      <div><Label className="mb-1.5 block">Réseaux</Label><ChipGroup testid="idea-network" options={PLATFORMS} values={networks} onChange={setNetworks} /></div>
      <div><Label className="mb-1.5 block">Formats</Label><ChipGroup testid="idea-format" options={FORMATS} values={formats} onChange={setFormats} /></div>
      <div><Label className="mb-1.5 block">Objectifs</Label><ChipGroup testid="idea-objective" options={OBJECTIVES} values={objectives} onChange={setObjectives} /></div>
      <div>
        <Label className="mb-1.5 block">Description — points essentiels à couvrir (optionnel)</Label>
        <textarea data-testid="idea-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
          placeholder="Ex : mettre en avant la nouvelle collection de bagues, annoncer l'offre -20 %, parler de la réouverture de l'atelier…"
          className="w-full text-sm border border-neutral-200 rounded-md px-3 py-2 bg-white" />
        <p className="text-[11px] text-neutral-400 mt-1">L'IA respectera ces consignes pour chaque idée générée.</p>
      </div>
      <Button data-testid="idea-generate-submit" onClick={generate} disabled={loading || networks.length === 0}
        className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5">
        {loading ? <><RefreshCw className="w-4 h-4 animate-spin" /> Génération en cours…</> : <><Sparkles className="w-4 h-4" /> Générer</>}
      </Button>
    </div>
  );
}

export function IdeaCard({ idea, color, onRefresh, index }) {
  const navigate = useNavigate();
  const createPub = async () => {
    try {
      const { data } = await api.post(`/ai/ideas/${idea.id}/to-publication`, {});
      toast.success("Publication créée — elle apparaît dans le calendrier");
      navigate(`/publications/${data.id}`);
    } catch { toast.error("Erreur"); }
  };
  const remove = async () => {
    toast.success("Idée supprimée");
    onRefresh?.();
  };
  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-5 animate-fade-up hover:-translate-y-0.5 transition-transform duration-200"
      style={{ animationDelay: `${index * 40}ms` }} data-testid={`idea-card-${idea.id}`}>
      <div className="flex items-center gap-2 mb-2.5">
        <span className="font-heading font-bold text-neutral-300 text-lg">{String(index + 1).padStart(2, "0")}</span>
        <span className="text-[11px] font-medium rounded-full px-2 py-0.5 text-white" style={{ background: color }}>{formatLabel(idea.format)}</span>
        {idea.objective && <span className="text-[11px] text-neutral-500 bg-neutral-100 rounded-full px-2 py-0.5 capitalize">{idea.objective}</span>}
      </div>
      <h4 className="font-heading font-bold leading-snug">{idea.title}</h4>
      {idea.description && <p className="text-sm text-neutral-500 mt-1.5 leading-relaxed line-clamp-3">{idea.description}</p>}
      {idea.angle && <p className="text-xs text-neutral-400 mt-2"><span className="font-medium text-neutral-500">Angle :</span> {idea.angle}</p>}
      {idea.cta && <p className="text-xs text-neutral-400 mt-1"><span className="font-medium text-neutral-500">CTA :</span> {idea.cta}</p>}
      <div className="flex gap-2 mt-4">
        <Button size="sm" data-testid={`idea-create-${idea.id}`} onClick={createPub}
          className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1 h-8 text-xs">
          <Wand2 className="w-3.5 h-3.5" /> Créer la publication
        </Button>
        <Button size="sm" variant="outline" data-testid={`idea-delete-${idea.id}`} onClick={remove} className="h-8 text-xs gap-1">
          <Trash2 className="w-3.5 h-3.5" /> Supprimer
        </Button>
      </div>
    </div>
  );
}
